export type TooltipSide = 'top' | 'bottom' | 'left' | 'right';
export type TooltipAlign = 'start' | 'center' | 'end';

export interface TooltipRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
}

export interface TooltipSize {
  width: number;
  height: number;
}

export interface TooltipPlacementOptions {
  triggerRect: TooltipRect;
  tooltipSize: TooltipSize;
  viewportWidth: number;
  viewportHeight: number;
  preferredSide?: TooltipSide;
  align?: TooltipAlign;
  sideOffset?: number;
  viewportPadding?: number;
  arrowPadding?: number;
}

export interface TooltipPlacement {
  side: TooltipSide;
  left: number;
  top: number;
  /** Offset from the tooltip's left edge for top/bottom placements. */
  arrowX: number;
  /** Offset from the tooltip's top edge for left/right placements. */
  arrowY: number;
}

const OPPOSITE_SIDE: Record<TooltipSide, TooltipSide> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
};

const PERPENDICULAR_SIDES: Record<TooltipSide, [TooltipSide, TooltipSide]> = {
  top: ['right', 'left'],
  bottom: ['right', 'left'],
  left: ['bottom', 'top'],
  right: ['bottom', 'top'],
};

const clamp = (value: number, min: number, max: number) => {
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
};

function alignedCoordinate(
  start: number,
  triggerSize: number,
  tooltipSize: number,
  align: TooltipAlign,
) {
  if (align === 'start') return start;
  if (align === 'end') return start + triggerSize - tooltipSize;
  return start + (triggerSize - tooltipSize) / 2;
}

function candidateForSide(
  side: TooltipSide,
  triggerRect: TooltipRect,
  tooltipSize: TooltipSize,
  align: TooltipAlign,
  sideOffset: number,
) {
  if (side === 'top' || side === 'bottom') {
    return {
      left: alignedCoordinate(triggerRect.left, triggerRect.width, tooltipSize.width, align),
      top: side === 'top'
        ? triggerRect.top - sideOffset - tooltipSize.height
        : triggerRect.bottom + sideOffset,
    };
  }

  return {
    left: side === 'left'
      ? triggerRect.left - sideOffset - tooltipSize.width
      : triggerRect.right + sideOffset,
    top: alignedCoordinate(triggerRect.top, triggerRect.height, tooltipSize.height, align),
  };
}

function overflowScore(
  candidate: { left: number; top: number },
  tooltipSize: TooltipSize,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number,
) {
  const right = candidate.left + tooltipSize.width;
  const bottom = candidate.top + tooltipSize.height;

  return (
    Math.max(0, viewportPadding - candidate.left)
    + Math.max(0, right - (viewportWidth - viewportPadding))
    + Math.max(0, viewportPadding - candidate.top)
    + Math.max(0, bottom - (viewportHeight - viewportPadding))
  );
}

function mainAxisOverflow(
  side: TooltipSide,
  candidate: { left: number; top: number },
  tooltipSize: TooltipSize,
  viewportWidth: number,
  viewportHeight: number,
  viewportPadding: number,
) {
  if (side === 'top') return Math.max(0, viewportPadding - candidate.top);
  if (side === 'bottom') {
    return Math.max(0, candidate.top + tooltipSize.height - (viewportHeight - viewportPadding));
  }
  if (side === 'left') return Math.max(0, viewportPadding - candidate.left);
  return Math.max(0, candidate.left + tooltipSize.width - (viewportWidth - viewportPadding));
}

/**
 * Resolves a viewport-safe tooltip placement using fixed-position coordinates.
 * The preferred side wins when it fits; otherwise the lowest-overflow side is
 * selected and the final cross-axis position is clamped to the viewport.
 */
export function resolveTooltipPlacement({
  triggerRect,
  tooltipSize,
  viewportWidth,
  viewportHeight,
  preferredSide = 'top',
  align = 'center',
  sideOffset = 8,
  viewportPadding = 8,
  arrowPadding = 12,
}: TooltipPlacementOptions): TooltipPlacement {
  const sideOrder: TooltipSide[] = [
    preferredSide,
    OPPOSITE_SIDE[preferredSide],
    ...PERPENDICULAR_SIDES[preferredSide],
  ];

  const candidates = new Map(sideOrder.map((side) => [
    side,
    candidateForSide(side, triggerRect, tooltipSize, align, sideOffset),
  ]));
  const preferredCandidate = candidates.get(preferredSide)!;
  const oppositeSide = OPPOSITE_SIDE[preferredSide];
  const oppositeCandidate = candidates.get(oppositeSide)!;

  let selectedSide = preferredSide;
  let selectedCandidate = preferredCandidate;
  let selectedScore = overflowScore(
    selectedCandidate,
    tooltipSize,
    viewportWidth,
    viewportHeight,
    viewportPadding,
  );

  const preferredMainOverflow = mainAxisOverflow(
    preferredSide,
    preferredCandidate,
    tooltipSize,
    viewportWidth,
    viewportHeight,
    viewportPadding,
  );

  if (preferredMainOverflow > 0) {
    const oppositeMainOverflow = mainAxisOverflow(
      oppositeSide,
      oppositeCandidate,
      tooltipSize,
      viewportWidth,
      viewportHeight,
      viewportPadding,
    );

    if (oppositeMainOverflow === 0) {
      selectedSide = oppositeSide;
      selectedCandidate = oppositeCandidate;
    } else {
      for (const side of sideOrder.slice(1)) {
        const candidate = candidates.get(side)!;
        const score = overflowScore(
          candidate,
          tooltipSize,
          viewportWidth,
          viewportHeight,
          viewportPadding,
        );

        if (score < selectedScore) {
          selectedSide = side;
          selectedCandidate = candidate;
          selectedScore = score;
        }
      }
    }
  }

  const left = clamp(
    selectedCandidate.left,
    viewportPadding,
    viewportWidth - viewportPadding - tooltipSize.width,
  );
  const top = clamp(
    selectedCandidate.top,
    viewportPadding,
    viewportHeight - viewportPadding - tooltipSize.height,
  );

  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;
  const maxArrowX = Math.max(arrowPadding, tooltipSize.width - arrowPadding);
  const maxArrowY = Math.max(arrowPadding, tooltipSize.height - arrowPadding);

  return {
    side: selectedSide,
    left,
    top,
    arrowX: clamp(triggerCenterX - left, arrowPadding, maxArrowX),
    arrowY: clamp(triggerCenterY - top, arrowPadding, maxArrowY),
  };
}
